import React, { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { KeyRound, Eye, EyeOff, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Self-service "Change my password" card. Lives on the Profile page.
 * Requires the caller to enter their current password + a new one at least
 * 8 characters long. Never shown for delegated / impersonated sessions.
 */
export default function ChangePasswordCard() {
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showCur, setShowCur] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const [saving, setSaving] = useState(false);

  const strength = (() => {
    const s = next || "";
    if (s.length < 8) return { label: "Too short", cls: "text-red-600", pct: 15 };
    let score = 0;
    if (/[a-z]/.test(s)) score += 1;
    if (/[A-Z]/.test(s)) score += 1;
    if (/[0-9]/.test(s)) score += 1;
    if (/[^A-Za-z0-9]/.test(s)) score += 1;
    if (s.length >= 12) score += 1;
    const map = {
      0: { label: "Weak", cls: "text-red-600", pct: 25 },
      1: { label: "Weak", cls: "text-red-600", pct: 35 },
      2: { label: "OK", cls: "text-amber-600", pct: 55 },
      3: { label: "Good", cls: "text-emerald-600", pct: 75 },
      4: { label: "Strong", cls: "text-emerald-700", pct: 90 },
      5: { label: "Very strong", cls: "text-emerald-700", pct: 100 },
    };
    return map[score];
  })();

  const canSubmit = cur && next && confirm && next === confirm && next.length >= 8 && next !== cur;

  const submit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: next });
      toast.success("Password updated");
      setCur(""); setNext(""); setConfirm("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update password");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="mt-8 border border-summit-hairline rounded-lg bg-white p-6" data-testid="change-password-card">
      <div className="flex items-center gap-2 mb-4">
        <KeyRound className="w-4 h-4 text-summit-primary" />
        <h2 className="font-display text-lg font-medium tracking-tight">Change password</h2>
      </div>
      <p className="text-xs text-gray-500 mb-5 max-w-md">
        Keep your account safe. Your new password must be at least <b>8 characters</b> and different from your current one.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl">
        <div className="md:col-span-2">
          <Label className="text-xs font-mono uppercase tracking-widest text-gray-500">Current password</Label>
          <div className="relative mt-1">
            <Input
              type={showCur ? "text" : "password"}
              value={cur}
              onChange={(e) => setCur(e.target.value)}
              autoComplete="current-password"
              className="pr-9 border-summit-hairline"
              data-testid="change-password-current"
            />
            <button
              type="button"
              onClick={() => setShowCur((s) => !s)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700 p-1"
              tabIndex={-1}
              aria-label={showCur ? "Hide" : "Show"}
            >
              {showCur ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>

        <div>
          <Label className="text-xs font-mono uppercase tracking-widest text-gray-500">New password</Label>
          <div className="relative mt-1">
            <Input
              type={showNext ? "text" : "password"}
              value={next}
              onChange={(e) => setNext(e.target.value)}
              autoComplete="new-password"
              className="pr-9 border-summit-hairline"
              data-testid="change-password-new"
            />
            <button
              type="button"
              onClick={() => setShowNext((s) => !s)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700 p-1"
              tabIndex={-1}
              aria-label={showNext ? "Hide" : "Show"}
            >
              {showNext ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          {next.length > 0 && (
            <div className="mt-1.5" data-testid="change-password-strength">
              <div className="h-1 bg-gray-100 rounded-full overflow-hidden">
                <div className={`h-full transition-all ${strength.pct >= 75 ? "bg-emerald-500" : strength.pct >= 55 ? "bg-amber-500" : "bg-red-500"}`} style={{ width: `${strength.pct}%` }} />
              </div>
              <div className={`text-[10px] font-mono uppercase tracking-widest mt-1 ${strength.cls}`}>{strength.label}</div>
            </div>
          )}
        </div>

        <div>
          <Label className="text-xs font-mono uppercase tracking-widest text-gray-500">Confirm new password</Label>
          <Input
            type={showNext ? "text" : "password"}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
            className="mt-1 border-summit-hairline"
            data-testid="change-password-confirm"
          />
          {confirm && next !== confirm && (
            <div className="text-[10px] font-mono uppercase tracking-widest text-red-600 mt-1">
              Passwords do not match
            </div>
          )}
          {confirm && next === confirm && next.length >= 8 && (
            <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-700 mt-1 flex items-center gap-1">
              <Check className="w-3 h-3" /> Matches
            </div>
          )}
        </div>
      </div>

      <div className="mt-5 flex items-center gap-3">
        <Button
          onClick={submit}
          disabled={!canSubmit || saving}
          className="bg-summit-primary hover:bg-summit-primary-hover text-white"
          data-testid="change-password-submit"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
          <span className="ml-2 text-sm">{saving ? "Updating…" : "Update password"}</span>
        </Button>
        {next && cur && next === cur && (
          <span className="text-xs text-red-600 font-mono uppercase tracking-widest">
            New must differ from current
          </span>
        )}
      </div>
    </section>
  );
}
